import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  appLanguageForCountry,
  isSupportedAppLanguage,
  USER_DETECTED_COUNTRY_CACHE_KEY,
} from '@/lib/supportedLanguages'

async function fetchCountryCodeFromIp(): Promise<string | null> {
  try {
    const ipResponse = await fetch('https://api.ipify.org?format=json', {
      headers: { Accept: 'application/json' },
    })
    if (!ipResponse.ok) return null

    const ipData = await ipResponse.json()
    const userIp = ipData.ip
    if (!userIp) return null

    const locationResponse = await fetch(`https://ip-api.com/json/${userIp}`, {
      headers: { Accept: 'application/json' },
    })
    if (!locationResponse.ok) return null

    const apiResponse = await locationResponse.json()
    if (apiResponse.status !== 'success') return null

    return apiResponse.countryCode?.trim().toUpperCase() || null
  } catch {
    return null
  }
}

function browserPreferredAppLanguage(): string | null {
  const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language]
  for (const browserLanguage of browserLanguages) {
    const languagePrefix = browserLanguage.split('-')[0]?.toLowerCase()
    if (languagePrefix && isSupportedAppLanguage(languagePrefix)) {
      return languagePrefix
    }
  }
  return null
}

export function useLanguageDetection(): void {
  const { i18n } = useTranslation()

  useEffect(() => {
    const applyDefaultLanguageIfNeeded = async () => {
      try {
        const savedLanguage = localStorage.getItem('i18nextLng')
        if (savedLanguage && isSupportedAppLanguage(savedLanguage)) {
          if (i18n.language !== savedLanguage) {
            await i18n.changeLanguage(savedLanguage)
          }
          return
        }

        const browserLanguage = browserPreferredAppLanguage()
        if (browserLanguage && browserLanguage !== 'en') {
          await i18n.changeLanguage(browserLanguage)
          return
        }

        await i18n.changeLanguage('en')

        const cachedCountryCode = localStorage.getItem(USER_DETECTED_COUNTRY_CACHE_KEY)
        let countryCode = cachedCountryCode?.trim().toUpperCase() || null

        if (!countryCode) {
          countryCode = await fetchCountryCodeFromIp()
          if (countryCode) {
            localStorage.setItem(USER_DETECTED_COUNTRY_CACHE_KEY, countryCode)
          }
        }

        const detectedLanguage = appLanguageForCountry(countryCode)
        if (detectedLanguage && i18n.language === 'en') {
          await i18n.changeLanguage(detectedLanguage)
        }
      } catch (error) {
        console.error('[useLanguageDetection] Error during language detection:', error)
        await i18n.changeLanguage('en')
      }
    }

    void applyDefaultLanguageIfNeeded()
  }, [i18n])
}
